// frontend/src/components/auth/StepUpModal.tsx
//
// Step-up reauthentication modal. Rendered by StepUpProvider when api.js's
// 401 STEP_UP_REQUIRED interceptor fires. The user re-enters their current
// password (and TOTP if `STEP_UP_TOTP_REQUIRED` comes back) and the request
// is then replayed automatically by the api.js interceptor.
//
// State machine:
//   idle → submitting → done                       (password only path)
//   idle → submitting → totp_required → submitting → done   (MFA path)
//   idle → submitting → error                      (any failure)
//
// On done, the modal calls onConfirm() which resolves the pending promise
// inside the api.js interceptor, which then re-issues the original request.
// On close/cancel, onCancel() rejects the promise so the original 401
// propagates to the caller.

import { useState, useEffect, useRef } from 'react'
import { Modal, Btn, Input } from '../ui/index.jsx'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'
import useSudoStore from '../../store/sudoStore.js'

interface StepUpModalProps {
  open: boolean
  scope: string | null
  onConfirm?: () => void
  onCancel?: () => void
}

export default function StepUpModal({ open, scope, onConfirm, onCancel }: StepUpModalProps) {
  const { t } = useLanguage()
  const setActive = useSudoStore(s => s.setActive)

  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const totpRef = useRef<HTMLInputElement>(null)

  // Reset state every time the modal opens for a fresh request.
  useEffect(() => {
    if (open) {
      setPassword(''); setTotp('')
      setNeedsTotp(false); setSubmitting(false); setError(null)
      // Autofocus the relevant field — delay one tick so the modal mounts first.
      setTimeout(() => passwordRef.current?.focus(), 30)
    }
  }, [open])

  useEffect(() => {
    if (needsTotp) setTimeout(() => totpRef.current?.focus(), 30)
  }, [needsTotp])

  async function submit(e: React.FormEvent) {
    e?.preventDefault?.()
    if (submitting) return
    setError(null); setSubmitting(true)
    try {
      const body: { password: string; scopes?: string[]; totp?: string } = {
        password,
        scopes: scope ? [scope] : undefined,
      }
      if (needsTotp) body.totp = totp.trim()
      const { data } = await api.post<{ expires_at?: string; scopes?: string[] }>('/auth/step-up', body)
      setActive({ expires_at: data.expires_at, scopes: data.scopes })
      onConfirm?.()
    } catch (err) {
      const code = (err as { response?: { data?: { error_code?: string; error?: string } } })?.response?.data?.error_code
      const msg  = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      if (code === 'STEP_UP_TOTP_REQUIRED') {
        setNeedsTotp(true)
        setError(null)
      } else if (code === 'STEP_UP_INVALID_TOTP') {
        setError(t('stepUpInvalidTotp'))
      } else if (code === 'STEP_UP_INVALID_PASSWORD') {
        setError(t('stepUpInvalidPassword'))
      } else {
        setError(msg || t('stepUpFailed'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  function close() {
    if (submitting) return
    onCancel?.()
  }

  return (
    <Modal open={open} onClose={close} title={t('stepUpRequired')} size="sm">
      <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          {t('stepUpPrompt')}
        </p>
        <Input
          ref={passwordRef}
          type="password"
          autoComplete="current-password"
          label={t('password')}
          placeholder={t('stepUpPasswordPlaceholder')}
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          disabled={submitting}
        />
        {needsTotp && (
          <Input
            ref={totpRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            label={t('stepUpTotpLabel')}
            placeholder={t('stepUpTotpPlaceholder')}
            value={totp}
            onChange={e => setTotp(e.target.value)}
            required
            disabled={submitting}
            maxLength={10}
          />
        )}
        {error && (
          <div role="alert" style={{ color: 'var(--color-error)', fontSize: 'var(--text-caption)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={close} disabled={submitting}>
            {t('cancel')}
          </Btn>
          <Btn type="submit" disabled={submitting || !password || (needsTotp && !totp)}>
            {submitting ? '…' : (t('stepUpConfirm'))}
          </Btn>
        </div>
      </form>
    </Modal>
  )
}
